"use client";

import { TypeModalWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { useModalManager } from "@2u-landing-pages/utils/useModalManager";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

interface ModalContextValue {
  isOpen: boolean;
  activeModalId: string | null;
  activeModal: TypeModalWithoutUnresolvableLinksResponse | null;
  openModal: (modalId: string) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
  modals: TypeModalWithoutUnresolvableLinksResponse[];
}

const LiveModalHandler: React.FC<{
  modal: TypeModalWithoutUnresolvableLinksResponse;
  onUpdate: (modal: TypeModalWithoutUnresolvableLinksResponse) => void;
}> = ({ modal, onUpdate }) => {
  const liveModal = useContentfulLiveUpdates(modal);

  useEffect(() => {
    onUpdate(liveModal);
  }, [liveModal, onUpdate]);

  return null;
};

export const ModalProvider: React.FC<ModalProviderProps> = ({ children, modals }) => {
  const [liveModals, setLiveModals] = useState<TypeModalWithoutUnresolvableLinksResponse[]>(modals);

  useEffect(() => {
    setLiveModals(modals);
  }, [modals]);

  const handleModalUpdate = useCallback((updatedModal: TypeModalWithoutUnresolvableLinksResponse) => {
    setLiveModals((prev) => prev.map((modal) => (modal.sys.id === updatedModal.sys.id ? updatedModal : modal)));
  }, []);

  const modalManager = useModalManager({ modals: liveModals });

  return (
    <ModalContext.Provider value={modalManager}>
      {children}
      {/* Handle live updates for each modal */}
      {modals.map((modal, index) => (
        <LiveModalHandler key={modal.sys.id || index} modal={modal} onUpdate={handleModalUpdate} />
      ))}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextValue | undefined => {
  const context = useContext(ModalContext);

  if (context === undefined) return;
  return context;
};
